
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Edit, Trash, Plus, Minus } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Mock data for demonstration
const mockItems = [
  {
    id: 1,
    category: "Barras Metálicas",
    type: "Metalons",
    material: "Aço carbono",
    thickness: "2 mm",
    dimension: "6m",
    quantity: 50,
  },
];

export function StockTable() {
  const [items, setItems] = useState(mockItems);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [deleteItemId, setDeleteItemId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();

  const filteredItems = items.filter((item) => {
    const matchesSearch =
      item.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.material.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "all" 
      ? true 
      : item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleDeleteClick = (id: number) => {
    setDeleteItemId(id);
  };

  const confirmDelete = () => {
    if (deleteItemId === null) return;
    
    setIsDeleting(true);
    
    // Simulando uma requisição para deletar o item
    setTimeout(() => {
      setItems(items.filter(item => item.id !== deleteItemId));
      setIsDeleting(false);
      setDeleteItemId(null);
      
      toast({
        title: "Item excluído",
        description: "O item foi removido do estoque com sucesso.",
      });
    }, 500);
  };

  const cancelDelete = () => {
    setDeleteItemId(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <Input
          placeholder="Buscar por tipo ou material..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 max-w-md bg-[#1A1F2C]"
        />
        <Select
          value={selectedCategory}
          onValueChange={setSelectedCategory}
        >
          <SelectTrigger className="w-[180px] bg-[#1A1F2C]">
            <SelectValue placeholder="Todas as categorias" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as categorias</SelectItem>
            <SelectItem value="Barras Metálicas">Barras Metálicas</SelectItem>
            <SelectItem value="Chapas de Aço">Chapas de Aço</SelectItem>
            <SelectItem value="Tubos Metálicos">Tubos Metálicos</SelectItem>
            <SelectItem value="Telas e Grades">Telas e Grades</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-md border border-gray-800">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#1A1F2C] hover:bg-[#1A1F2C]">
              <TableHead className="text-white">Categoria</TableHead>
              <TableHead className="text-white">Tipo</TableHead>
              <TableHead className="text-white">Material</TableHead>
              <TableHead className="text-white">Espessura</TableHead>
              <TableHead className="text-white">Dimensão</TableHead>
              <TableHead className="text-white">Quantidade</TableHead>
              <TableHead className="text-right text-white">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredItems.map((item) => (
              <TableRow key={item.id} className="hover:bg-[#2A2F3C]">
                <TableCell>{item.category}</TableCell>
                <TableCell>{item.type}</TableCell>
                <TableCell>{item.material}</TableCell>
                <TableCell>{item.thickness}</TableCell>
                <TableCell>{item.dimension}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span>{item.quantity}</span>
                    <div className="flex gap-1">
                      <Button variant="outline" size="icon" className="h-8 w-8">
                        <Plus className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="icon" className="h-8 w-8">
                        <Minus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 hover:bg-[#E60026] hover:text-white"
                      onClick={() => handleDeleteClick(item.id)}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={deleteItemId !== null} onOpenChange={(open) => !open && cancelDelete()}>
        <AlertDialogContent className="bg-[#1A1F2C] border-gray-700 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-['Russo_One']">Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300">
              Você está prestes a excluir este item do estoque.
              <br />
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="bg-transparent border border-gray-600 text-white hover:bg-gray-700"
              disabled={isDeleting}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-[#E60026] hover:bg-[#cc0022] text-white"
              disabled={isDeleting}
            >
              {isDeleting ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
